/**
 * Admin API client wrapping the backend /admin/* REST endpoints.
 * Provides paginated fetches for AI bounties, AI submissions, bounties,
 * submissions, payments, and users; create/delete operations for those
 * resources; and test email/SMS sending utilities.
 * Consumed by admin UI such as PaginatedList, BountyCreateEdit, and the
 * submission pages (SubmissionCreate, SubmissionDisplay).
 */
import { api } from "@/lib/axios";
import { fetchPaginatedItems, PaginationQueryParams } from "@/api/paginated";
import { AiBounty } from "@/types/aiBounty";
import { AiSubmission } from "@/types/aiSubmission";
import { Bounty } from "@/types/bounty";
import { PaginatedResponse } from "@/types/pagination";
import { PaymentBountySubmission } from "@/types/payment";
import { SubmissionDisplay } from "@/types/submission";
import { PrivateUser } from "@/types/user";

export const AdminAPI = {
    // Fetch operations
    fetchAiBounties: async (
        params: PaginationQueryParams = {}
    ): Promise<PaginatedResponse<AiBounty>> => {
        return fetchPaginatedItems<AiBounty>("/admin/ai-bounties", params);
    },
    fetchAiSubmissions: async (
        params: PaginationQueryParams = {}
    ): Promise<PaginatedResponse<AiSubmission>> => {
        return fetchPaginatedItems<AiSubmission>("/admin/ai-submissions", params);
    },
    fetchBounties: async (
        params: PaginationQueryParams = {}
    ): Promise<PaginatedResponse<Bounty>> => {
        return fetchPaginatedItems<Bounty>("/admin/bounties", params);
    },
    fetchSubmissions: async (
        params: PaginationQueryParams = {}
    ): Promise<PaginatedResponse<SubmissionDisplay>> => {
        return fetchPaginatedItems<SubmissionDisplay>("/admin/submissions", params);
    },
    fetchPayments: async (
        params: PaginationQueryParams = {}
    ): Promise<PaginatedResponse<PaymentBountySubmission>> => {
        return fetchPaginatedItems<PaymentBountySubmission>("/admin/payments", params);
    },
    fetchUsers: async (
        params: PaginationQueryParams = {}
    ): Promise<PaginatedResponse<PrivateUser>> => {
        return fetchPaginatedItems<PrivateUser>("/admin/users", params);
    },

    // Crud operations
    createAiBounty: async (aiBounty: AiBounty) => {
        const { data } = await api.post<{ aiBounty: AiBounty }>(`/admin/ai-bounties`, {
            aiBounty,
        });
        return data;
    },
    createAiSubmission: async (aiSubmission: AiSubmission) => {
        const { data } = await api.post<{ aiSubmission: AiSubmission }>(`/admin/ai-submissions/`, {
            aiSubmission,
        });
        return data;
    },
    deleteAiBounty: async (aiBountyId: number) => {
        const { data } = await api.delete(`/admin/ai-bounties/${aiBountyId}`);
        return data;
    },
    deleteAiSubmission: async (aiSubmissionId: number) => {
        const { data } = await api.delete(`/admin/ai-submissions/${aiSubmissionId}`);
        return data;
    },
    deleteSubmission: async (submissionId: number) => {
        const { data } = await api.delete(`/admin/submissions/${submissionId}`);
        return data;
    },
    deleteBounty: async (bountyId: number) => {
        const { data } = await api.delete(`/admin/bounties/${bountyId}`);
        return data;
    },
    deleteUser: async (userId: number) => {
        const { data } = await api.delete(`/admin/users/${userId}`);
        return data;
    },

    // Communication
    sendTestEmail: async (email: string, subject: string, body: string) => {
        const { data } = await api.post(`/admin/send-test-email`, { email, subject, body });
        return data;
    },
    sendTestSMS: async (phone: string, message: string) => {
        const { data } = await api.post(`/admin/send-test-sms`, { phone, message });
        return data;
    },
};
